import type { FC } from 'react'
import { useCallback } from 'react'
import type { SxProps, Theme } from '@mui/material'
import {
  Box,
  CircularProgress,
  Divider,
  ListItemIcon,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material'
import { LogoutOutlined } from '@mui/icons-material'
import { useAuthActions } from '../hooks'

interface UserDropdownMenuProps {
  readonly anchorEl: HTMLElement | null
  readonly isOpen: boolean
  readonly onClose: () => void
  readonly displayName: string
  readonly email?: string | null
}

export const UserDropdownMenu: FC<UserDropdownMenuProps> = ({
  anchorEl,
  isOpen,
  onClose,
  displayName,
  email,
}) => {
  const { logout, isLoggingOut } = useAuthActions()

  const handleLogout = useCallback(() => {
    onClose()
    logout().catch(() => {})
  }, [logout, onClose])

  return (
    <Menu
      anchorEl={anchorEl}
      open={isOpen}
      onClose={onClose}
      transformOrigin={{ horizontal: 'left', vertical: 'bottom' }}
      anchorOrigin={{ horizontal: 'left', vertical: 'top' }}
      sx={menuSx}
    >
      <Box sx={headerSx}>
        <Typography variant="subtitle2" sx={headerNameSx}>
          {displayName}
        </Typography>
        {email && (
          <Typography variant="caption" sx={headerEmailSx}>
            {email}
          </Typography>
        )}
      </Box>

      <Divider sx={dividerSx} />

      <MenuItem onClick={handleLogout} disabled={isLoggingOut} sx={menuItemSx}>
        <ListItemIcon sx={iconContainerSx}>
          {isLoggingOut ? (
            <CircularProgress size={16} sx={spinnerSx} />
          ) : (
            <LogoutOutlined fontSize="small" />
          )}
        </ListItemIcon>
        <Typography variant="body2">{isLoggingOut ? 'Signing Out...' : 'Sign Out'}</Typography>
      </MenuItem>
    </Menu>
  )
}

const menuSx: SxProps<Theme> = {
  '& .MuiPaper-root': {
    minWidth: 220,
    borderRadius: 2,
    border: '1px solid',
    borderColor: 'divider',
    boxShadow: 8,
  },
}

const headerSx: SxProps<Theme> = {
  px: 2,
  py: 1.5,
}

const headerNameSx: SxProps<Theme> = {
  fontWeight: 600,
  color: 'text.primary',
}

const headerEmailSx: SxProps<Theme> = {
  display: 'block',
  color: 'text.secondary',
  mt: 0.25,
}

const dividerSx: SxProps<Theme> = {
  my: 0.5,
}

const menuItemSx: SxProps<Theme> = {
  mx: 1,
  mb: 1,
  borderRadius: 1,
  color: 'text.secondary',
  '&:hover': {
    backgroundColor: 'action.hover',
    color: 'text.primary',
  },
  '&.Mui-disabled': {
    color: 'text.disabled',
  },
}

const iconContainerSx: SxProps<Theme> = {
  minWidth: 32,
}

const spinnerSx: SxProps<Theme> = {
  color: 'text.disabled',
}
